import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getMonitors,
  getMonitorInfo,
  getServeRule,
  createMonitor,
  updateMonitor,
  deleteMonitor,
} from "../api/monitors";
import type {
  CreateMonitorRequest,
  UpdateMonitorRequest,
} from "../api/monitors";
import type { ServeRule } from "../pages/ordering/components/tables/serveRule";
import { useIsEdgeLocal } from "./useEdgeMode";

export const useMonitors = () => {
  return useQuery({
    queryKey: ["monitors"],
    queryFn: getMonitors,
  });
};

export const useMonitorInfo = (monitorId: number | null) => {
  return useQuery({
    queryKey: ["monitors", "info", monitorId],
    queryFn: () => getMonitorInfo(monitorId!),
    enabled: monitorId !== null,
  });
};

// Кратко: правилото се мени само от Конфигурация → Монитори (там запис
// инвалидира ["monitors"]), а другите терминали го хващат при фокус.
const SERVE_RULE_STALE_MS = 30_000;

/**
 * От кой кухненски статус ред може да се сервира в Маркиране (GET
 * /monitors/serve-rule) — по станциите на реда, с таван по тях (serveRule.ts).
 * null — без ограничение: заведение без сервитьорски монитор или грешка при
 * четенето. В локален режим отговаря касата — от правилото в snapshot-а, по
 * което сама проверява (FiscalAPI `edge/serving.py`): без интернет е същото
 * като с интернет. BE/касата правят същата проверка при PATCH
 * /bills/items/served.
 */
export const useServeRule = (): ServeRule | null => {
  // Смяната облак ↔ каса пита наново (друг източник на същото правило).
  const isEdgeLocal = useIsEdgeLocal();
  const { data } = useQuery({
    queryKey: ["monitors", "serve-rule", isEdgeLocal],
    queryFn: getServeRule,
    staleTime: SERVE_RULE_STALE_MS,
    refetchOnWindowFocus: true,
    retry: false,
  });
  if (data?.serveFromStatus == null) return null;
  return {
    serveFromStatusByStation: data.serveFromStatusByStation ?? null,
    serveFromStatus: data.serveFromStatus,
    reachableStatusByStation: data.reachableStatusByStation ?? {},
  };
};

export const useCreateMonitor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateMonitorRequest) => createMonitor(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["monitors"] });
    },
  });
};

export const useUpdateMonitor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateMonitorRequest }) =>
      updateMonitor(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["monitors"] });
    },
  });
};

export const useDeleteMonitor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteMonitor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["monitors"] });
    },
  });
};
